import React from 'react';
import { useTranslation } from 'react-i18next';

import { HOSTS } from './Text.styles';

/**
 * Translation-ready text for the nova area.
 *
 * It runs the value through i18next (the `text` value doubles as the i18n
 * key/default, so literal English copy renders as-is until a translation is
 * supplied) and renders the result as a **normal, auto-escaped React text
 * node**.
 *
 * SECURITY: this deliberately does NOT forward to the project-wide `<Text>`
 * page-component (`@/page-components/Text/Text`). That component renders every
 * branch through `dangerouslySetInnerHTML`, and i18next runs with
 * `interpolation.escapeValue: false`, so routing dynamic / API / remote data
 * through it is an XSS sink. Here the translated string is a React child, which
 * React escapes — so passing API values (brand/feature/catalog names) is safe.
 *
 * The API is unchanged, so it still composes with styled-components:
 *
 *   const Title = styled(Text)`font-size: 22px; font-weight: 600;`;
 *   <Title text="campaigns.title" tag="span" />
 */
export type TextTag = 'div' | 'span' | 'paragraph' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

export type TextProps = {
  /** i18n key or literal copy. Translated via i18next, then rendered auto-escaped. */
  text: string;
  /** Rendered HTML element. `paragraph` maps to <p>. Defaults to <span>. */
  tag?: TextTag;
  /** Raw CSS string applied to the host element (rarely needed; prefer styled(Text)). */
  styleText?: string;
  /** Injected by styled-components when wrapped with styled(Text). */
  className?: string;
  /** i18n interpolation values. Escaped on render (React-escaped as a text node). */
  placeholders?: Record<string, string | number>;
  title?: string;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  /** Skip i18n and render `text` verbatim. For values already in their target language
   *  (e.g. language endonyms) that must NOT re-translate to the active UI language. */
  raw?: boolean;
};

export const Text = ({
  text,
  tag = 'span',
  styleText = '',
  className,
  placeholders,
  title,
  onClick,
  raw = false,
}: TextProps): React.ReactElement => {
  const { t } = useTranslation();
  const Host = HOSTS[tag === 'paragraph' ? 'p' : tag] ?? HOSTS.span;
  // t() returns the (possibly interpolated) string; rendering it as a child
  // means React escapes it — no dangerouslySetInnerHTML, no XSS.
  const content = raw ? text : placeholders ? t(text, placeholders) : t(text);
  return (
    <Host className={className ?? ''} $styleText={styleText} title={title} onClick={onClick}>
      {content}
    </Host>
  );
};

export default Text;
